/**
 * One read-time rule for "are orders / quotes available" and "what does the New button create".
 *
 * Before (SETTINGS-WIRING fix 9) each screen had its own test:
 *  - sidebar: `salesQuotes !== false` (unset → Quotes shown), dashboard/QuickFind/bottom bar: `!!salesQuotes`
 *    (unset → hidden);
 *  - classic and V2 dashboards also checked the legacy `features.orders.enabled`;
 *  - the sidebar's main button said "New Quote" whenever quotes were *available* (salesQuotes) but always
 *    opened a new *order*; the V2 header showed "New Quote" in quote mode even when quotes were off.
 *
 * Rule (authoritative first, legacy second, then a default):
 *  - orders: `enableOrders` → legacy `orders.enabled` → on;
 *  - quotes: `enableQuotes` → legacy `salesQuotes` → QUOTES_WHEN_UNSET (off: the settings default and BC's
 *    default; the worker applies the same rule, see workers/src/middleware/portal-access.ts quotesOn);
 *  - quote mode: `docMode === 'quote'` → legacy `quoteWorkflow`.
 */
export interface DocumentFeatureFlags {
  enableOrders?: boolean;
  enableQuotes?: boolean;
  docMode?: 'order' | 'quote' | string;
  salesQuotes?: boolean;
  quoteWorkflow?: boolean;
  orders?: { enabled?: boolean } | null;
}

export const QUOTES_WHEN_UNSET = false;

type Features = DocumentFeatureFlags | null | undefined;

export function ordersEnabled(f: Features): boolean {
  if (typeof f?.enableOrders === 'boolean') {return f.enableOrders;}
  if (typeof f?.orders?.enabled === 'boolean') {return f.orders.enabled;}
  return true;
}

export function quotesEnabled(f: Features): boolean {
  if (typeof f?.enableQuotes === 'boolean') {return f.enableQuotes;}
  if (typeof f?.salesQuotes === 'boolean') {return f.salesQuotes;}
  return QUOTES_WHEN_UNSET;
}

export function quoteModeDefault(f: Features): boolean {
  if (f?.docMode === 'quote' || f?.docMode === 'order') {return f.docMode === 'quote';}
  return f?.quoteWorkflow === true;
}

/**
 * What the primary "New" button creates, or null when neither is available.
 * Quote mode wins only when quotes are on; with orders off but quotes on, offer a quote rather than nothing.
 */
export function primaryNewDocument(f: Features): 'order' | 'quote' | null {
  const quotes = quotesEnabled(f);
  if (quotes && quoteModeDefault(f)) {return 'quote';}
  if (ordersEnabled(f)) {return 'order';}
  return quotes ? 'quote' : null;
}

/**
 * Non-item lines (G/L account, resource, charge …) are shown only when the tenant switches them on.
 * Off by default, matching BC's InitValue for "Show Non-Item Lines" (decided 18 Oct 2026).
 */
export function showNonItemLines(f: { showNonItemLines?: boolean } | null | undefined): boolean {
  return f?.showNonItemLines === true;
}

/**
 * Credit memo history: on unless switched off, matching BC (Portal Setup "Enable Credit Memos"
 * InitValue = true) and the Features tab default. The sidebar already used this rule, but both
 * dashboards and the data load needed an explicit true, so for tenants that never set it the
 * Credit Memos nav item led nowhere and the history never loaded. One rule for every surface.
 */
export function creditMemosEnabled(f: { creditMemos?: boolean } | null | undefined): boolean {
  return f?.creditMemos !== false;
}

/** Invoice history: on unless switched off (BC "Enable Invoices" InitValue = true). */
export function invoicesEnabled(f: { invoices?: boolean } | null | undefined): boolean {
  return f?.invoices !== false;
}

/** Posted shipments: on unless switched off (BC "Enable Shipments" InitValue = true). */
export function shipmentsEnabled(f: { shipments?: boolean } | null | undefined): boolean {
  return f?.shipments !== false;
}

/** Order lines to display: item lines always; non-item lines (editable === false) only when switched on. */
export function visibleOrderLines<T extends { editable?: boolean }>(lines: T[], f: { showNonItemLines?: boolean } | null | undefined): T[] {
  return showNonItemLines(f) ? lines : lines.filter(line => line.editable !== false);
}
